'use client';

import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { AUTO_REPLIES_FORM_TITLE } from '@/components/auto-replies-copy';
import type { AutoReplyScenarioEntity } from '@/contracts/marketplace/auto-replies';
import { useAuth } from '@/context/auth.context';
import { useAutoRepliesModal } from '@/context/auto-replies-modal.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useReviewsPageProgressContext, useReviewsPageSelectionContext } from '@/context/reviews-page.context';
import { UIPriority } from '@/enums/ui-priorities';
import { extractApiErrorMessageFromUnknown } from '@/shared/errors/api-error';
import { normalizeStringList } from '@/shared/normalize-string-list';
import { resolveOperationId } from '@/shared/operation-id';
import { getSelectedAutoRepliesRunLabels } from '@/shared/auto-replies-labels';
import Button from '@/ui/Button';
import Modal from '@/ui/Modal';
import { toast } from '@/ui/toast';
import clsx from 'clsx';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SelectedAutoRepliesRunScope =
    | {
          type: 'selectionSession';
          sessionId: string;
          selectedCount: number;
      }
    | {
          type: 'reviewFeedItemIds';
          reviewFeedItemIds: string[];
      };

type SelectedAutoRepliesModalProps = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    runScope?: SelectedAutoRepliesRunScope;
};

const SELECTED_AUTO_REPLIES_LABELS = getSelectedAutoRepliesRunLabels('ru');
const SELECTED_AUTO_REPLIES_LOAD_ERROR = 'Не удалось загрузить сценарии автоответов';
const SELECTED_AUTO_REPLIES_RUN_ERROR = 'Не удалось запустить автоответы';

const selectedAutoRepliesModalLogger = createClientLogger({
    scope: 'selected-auto-replies-modal',
});

const getEnabledScenarioIds = (scenarios: AutoReplyScenarioEntity[]): string[] =>
    scenarios.filter((scenario) => scenario.enabled).map((scenario) => scenario.id);

const sortScenariosByPriority = (scenarios: AutoReplyScenarioEntity[]): AutoReplyScenarioEntity[] =>
    [...scenarios].sort((left, right) => left.priority - right.priority || left.name.localeCompare(right.name));

const normalizeSelectedAutoRepliesRunScope = (
    runScope: SelectedAutoRepliesRunScope | undefined,
    fallbackSelectionScope: SelectedAutoRepliesRunScope | null,
): SelectedAutoRepliesRunScope | null => {
    const resolvedScope = runScope ?? fallbackSelectionScope;
    if (!resolvedScope) {
        return null;
    }

    if (resolvedScope.type === 'reviewFeedItemIds') {
        const reviewFeedItemIds = normalizeStringList(resolvedScope.reviewFeedItemIds);
        return reviewFeedItemIds.length
            ? {
                  type: 'reviewFeedItemIds',
                  reviewFeedItemIds,
              }
            : null;
    }

    const sessionId = resolvedScope.sessionId.trim();
    return sessionId
        ? {
              type: 'selectionSession',
              sessionId,
              selectedCount: Math.max(0, resolvedScope.selectedCount),
          }
        : null;
};

const getSelectedAutoRepliesRunScopeCount = (runScope: SelectedAutoRepliesRunScope | null): number => {
    if (!runScope) {
        return 0;
    }

    return runScope.type === 'reviewFeedItemIds' ? runScope.reviewFeedItemIds.length : runScope.selectedCount;
};

export const SelectedAutoRepliesModal: FC<SelectedAutoRepliesModalProps> = ({ isShown, setIsShown, runScope }) => {
    const { accessToken } = useAuth();
    const { reviewsApi } = useMarketplaceApi();
    const { selectionSessionId, pickedReviews } = useReviewsPageSelectionContext();
    const { setAutoReplyProgress } = useReviewsPageProgressContext();
    const { openAutoRepliesModal } = useAutoRepliesModal();

    const [scenarios, setScenarios] = useState<AutoReplyScenarioEntity[]>([]);
    const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [loadErrorText, setLoadErrorText] = useState<string | null>(null);
    const [runErrorText, setRunErrorText] = useState<string | null>(null);
    const canResumeAfterEditorCloseRef = useRef(false);

    const sortedScenarios = useMemo(() => sortScenariosByPriority(scenarios), [scenarios]);
    const enabledScenarioIds = useMemo(() => getEnabledScenarioIds(sortedScenarios), [sortedScenarios]);
    const selectedScenarioIdSet = useMemo(() => new Set(selectedScenarioIds), [selectedScenarioIds]);
    const isAllEnabledSelected =
        enabledScenarioIds.length > 0 &&
        enabledScenarioIds.every((scenarioId) => selectedScenarioIdSet.has(scenarioId));
    const fallbackSelectionScope = useMemo<SelectedAutoRepliesRunScope | null>(
        () =>
            selectionSessionId
                ? {
                      type: 'selectionSession',
                      sessionId: selectionSessionId,
                      selectedCount: pickedReviews.length,
                  }
                : null,
        [pickedReviews.length, selectionSessionId],
    );
    const activeRunScope = useMemo(
        () => normalizeSelectedAutoRepliesRunScope(runScope, fallbackSelectionScope),
        [fallbackSelectionScope, runScope],
    );
    const activeRunScopeCount = getSelectedAutoRepliesRunScopeCount(activeRunScope);
    const canRun = !isLoading && !isRunning && activeRunScopeCount > 0 && selectedScenarioIds.length > 0;

    useEffect(() => {
        canResumeAfterEditorCloseRef.current = activeRunScopeCount > 0;
    }, [activeRunScopeCount]);

    useEffect(() => {
        if (!isShown) {
            return;
        }

        let ignore = false;
        setIsLoading(true);
        setLoadErrorText(null);
        setRunErrorText(null);

        void reviewsApi
            .getAutoReplyScenarios({ authToken: accessToken })
            .then((response) => {
                if (ignore) {
                    return;
                }

                const nextScenarios = sortScenariosByPriority(response.data);
                setScenarios(nextScenarios);
                setSelectedScenarioIds(getEnabledScenarioIds(nextScenarios));
            })
            .catch((error: unknown) => {
                if (ignore) {
                    return;
                }

                const message = extractApiErrorMessageFromUnknown(error) ?? SELECTED_AUTO_REPLIES_LOAD_ERROR;
                setLoadErrorText(message);
                setScenarios([]);
                setSelectedScenarioIds([]);
                logClientWarning({
                    logger: selectedAutoRepliesModalLogger,
                    message: '[client.auto-replies.selected-run.scenarios-load] Failed to load selected-run scenarios.',
                    error,
                    context: {
                        operation: 'selected_auto_replies_scenarios_load',
                    },
                });
            })
            .finally(() => {
                if (!ignore) {
                    setIsLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
    }, [accessToken, isShown]);

    const setScenarioSelected = useCallback((scenarioId: string, checked: boolean) => {
        setSelectedScenarioIds((currentIds) => {
            const normalizedIds = normalizeStringList(currentIds);
            const selectedIds = new Set(normalizedIds);
            if (checked) {
                selectedIds.add(scenarioId);
            } else {
                selectedIds.delete(scenarioId);
            }

            return normalizedIds.includes(scenarioId)
                ? normalizedIds.filter((id) => selectedIds.has(id))
                : [...normalizedIds, ...[scenarioId].filter((id) => selectedIds.has(id))];
        });
    }, []);

    const toggleAllEnabledScenarios = useCallback(() => {
        setSelectedScenarioIds(isAllEnabledSelected ? [] : enabledScenarioIds);
    }, [enabledScenarioIds, isAllEnabledSelected]);

    const runSelectedAutoReplies = useCallback(async () => {
        if (!activeRunScope || activeRunScopeCount === 0) {
            toast.warning('Выберите отзывы');
            return;
        }

        const scenarioIds = normalizeStringList(selectedScenarioIds);
        if (!scenarioIds.length) {
            toast.warning('Выберите сценарии автоответов');
            return;
        }

        setIsRunning(true);
        setRunErrorText(null);
        try {
            const operationStartedAt = Date.now();
            const scopePayload =
                activeRunScope.type === 'reviewFeedItemIds'
                    ? { reviewFeedItemIds: activeRunScope.reviewFeedItemIds }
                    : { sessionId: activeRunScope.sessionId };
            const response = await reviewsApi.enqueueSelectedAutoRepliesRun(
                {
                    ...scopePayload,
                    scenarioIds,
                    operationId: resolveOperationId(undefined, operationStartedAt),
                },
                { authToken: accessToken },
            );

            if (response.data.limitWarning) {
                toast.warning(response.data.limitWarning);
            }
            if (response.data.skipped.length) {
                toast.warning(`Пропущено отзывов: ${response.data.skipped.length}`);
            }
            setAutoReplyProgress({
                isRunning: true,
                operationId: response.data.operationId,
                startedAt: response.data.startedAt,
                total: response.data.total,
                processed: 0,
                successCount: 0,
                skippedCount: 0,
                failedCount: 0,
                etaSeconds: null,
                terminalStage: null,
                errorMessage: null,
            });
            toast.success(`Автоответы запущены: ${response.data.total}`);
            setIsShown(false);
        } catch (error) {
            const message = extractApiErrorMessageFromUnknown(error) ?? SELECTED_AUTO_REPLIES_RUN_ERROR;
            setRunErrorText(message);
            toast.error(message);
            logClientWarning({
                logger: selectedAutoRepliesModalLogger,
                message: '[client.auto-replies.selected-run.enqueue] Failed to enqueue selected auto-replies run.',
                error,
                context: {
                    operation: 'selected_auto_replies_run',
                    selectionScope: activeRunScope.type,
                    reviewFeedItemIdsCount:
                        activeRunScope.type === 'reviewFeedItemIds'
                            ? activeRunScope.reviewFeedItemIds.length
                            : undefined,
                    pickedReviewsCount: activeRunScopeCount,
                    selectedScenarioIdsCount: scenarioIds.length,
                },
            });
        } finally {
            setIsRunning(false);
        }
    }, [
        activeRunScope,
        activeRunScopeCount,
        accessToken,
        reviewsApi,
        selectedScenarioIds,
        setAutoReplyProgress,
        setIsShown,
    ]);

    const resumeSelectedRunModal = useCallback(() => {
        if (!canResumeAfterEditorCloseRef.current) {
            selectedAutoRepliesModalLogger.info(
                '[client.auto-replies.selected-run-resume] Skipped selected auto-replies modal resume after editor close.',
                {
                    outcome: 'skipped',
                    reason: 'selection_unavailable',
                },
            );
            return;
        }

        setIsShown(true);
    }, [setIsShown]);

    const openScenarioEditor = useCallback(() => {
        setIsShown(false);
        openAutoRepliesModal({ onClose: resumeSelectedRunModal });
    }, [openAutoRepliesModal, resumeSelectedRunModal, setIsShown]);

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title={SELECTED_AUTO_REPLIES_LABELS.title}
            closable
            className="h-[min(640px,calc(100vh-24px))] w-[min(560px,calc(100vw-16px))]"
        >
            <div className="flex h-full min-h-0 w-full flex-col gap-3 p-4">
                <div className="flex min-h-0 grow flex-col gap-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300"
                            checked={isAllEnabledSelected}
                            disabled={!enabledScenarioIds.length || isLoading}
                            onChange={toggleAllEnabledScenarios}
                        />
                        <span>Все сценарии</span>
                    </label>
                    <div className="min-h-0 grow overflow-auto rounded-md border border-slate-200">
                        {isLoading ? (
                            <div className="flex h-full items-center justify-center p-6 text-sm text-slate-500">
                                Загрузка сценариев...
                            </div>
                        ) : loadErrorText ? (
                            <div className="flex h-full items-center justify-center p-6 text-sm text-red-600">
                                {loadErrorText}
                            </div>
                        ) : !sortedScenarios.length ? (
                            <div className="flex h-full items-center justify-center p-6 text-sm text-slate-500">
                                Сценариев пока нет
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {sortedScenarios.map((scenario) => (
                                    <label
                                        key={scenario.id}
                                        className={clsx(
                                            'flex items-start gap-3 px-3 py-2 text-sm',
                                            scenario.enabled ? 'text-slate-800' : 'text-slate-400',
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 h-4 w-4 rounded border-slate-300"
                                            checked={scenario.enabled && selectedScenarioIdSet.has(scenario.id)}
                                            disabled={!scenario.enabled || isRunning}
                                            onChange={(event) => setScenarioSelected(scenario.id, event.target.checked)}
                                        />
                                        <span className="min-w-0 break-words">{scenario.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {runErrorText ? <p className="text-xs text-red-600">{runErrorText}</p> : null}
                <div className="grid grid-cols-2 gap-2">
                    <Button
                        type="button"
                        priorityType={UIPriority.SECONDARY}
                        uiSize="compact"
                        onClick={openScenarioEditor}
                    >
                        {AUTO_REPLIES_FORM_TITLE}
                    </Button>
                    <Button
                        type="button"
                        priorityType={UIPriority.PRIMARY}
                        uiSize="compact"
                        disabled={!canRun}
                        onClick={() => void runSelectedAutoReplies()}
                    >
                        {isRunning ? 'Запускаем...' : 'Запустить'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
